import { useMemo, useState, type ReactElement } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Plug, Search } from "lucide-react";
import {
  Button,
  EmptyState,
  Input,
  Link,
  SegmentedControl,
  StatusPill,
  DataTable,
  type DataTableColumn,
} from "@playbookai/ui";
import type {
  IntegrationTemplate,
  TenantIntegration,
} from "@playbookai/api-clients";
import {
  ADMIN_INTEGRATIONS_NEW_ROUTE,
  adminIntegrationRoute,
} from "../../../routes.js";
import {
  buildInventoryRows,
  filterInventory,
  LIFECYCLE_LABELS,
  LIFECYCLE_TONES,
  type InventoryRow,
  type LifecycleFilter,
  PROTOCOL_LABELS,
} from "./integration-inventory.js";
import { connectionModeLabel } from "./connection-mode.js";

/**
 * The administrator's integration inventory — what this workspace has
 * configured, and nothing else.
 *
 * The permanent "Available" catalogue that used to sit below this list is gone.
 * It answered a question an administrator asks once ("what could I add?") on
 * every visit, beneath the list they actually came for, and it made the page
 * grow with the deployment's connector count rather than with the tenant's
 * decisions. Adding is now a route — one primary action here, a page of its own
 * there — which is also what lets the OAuth round trip in that flow come back
 * to a URL instead of to a piece of component state.
 *
 * NO PROVIDER NAME REACHES THIS FILE. A row's second line is the template's own
 * `displayName` and the contract's protocol word; both are data.
 */

/**
 * Below this, a search box is furniture. The floor is measured against the
 * UNFILTERED count, never the visible one, so filtering can never remove the
 * control that would undo it.
 */
const SEARCH_FLOOR = 2;

export function IntegrationsInventory({
  integrations,
  templates,
}: {
  readonly integrations: readonly TenantIntegration[];
  readonly templates: readonly IntegrationTemplate[];
}): ReactElement {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [lifecycle, setLifecycle] = useState<LifecycleFilter>("all");

  const rows = useMemo(
    () => buildInventoryRows(integrations, templates),
    [integrations, templates],
  );
  const visible = useMemo(
    () => filterInventory(rows, query, lifecycle),
    [rows, query, lifecycle],
  );

  const filtering = query.trim() !== "" || lifecycle !== "all";
  const clearFilters = (): void => {
    setQuery("");
    setLifecycle("all");
  };

  /* The page header (title, description, Add integration) is the section's,
     rendered by IntegrationsSettings through AdminSection (PB-5178); this
     component owns the search, the filter and the list. */
  return (
    <div className="space-y-6">
      {rows.length >= SEARCH_FLOOR ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <Input
              type="search"
              aria-label="Search integrations"
              placeholder="Search integrations"
              prefix={<Search className="size-3.5" aria-hidden="true" />}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <SegmentedControl<LifecycleFilter>
            aria-label="Filter integrations by status"
            className="shrink-0"
            options={filterOptions(rows)}
            value={lifecycle}
            onChange={setLifecycle}
          />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          icon={<Plug className="w-5 h-5" />}
          title="No integrations yet"
          description="Add one to let this workspace reach an outside system. It stays switched off until you turn it on."
          action={
            <Button
              variant="primary"
              onClick={() => navigate(ADMIN_INTEGRATIONS_NEW_ROUTE)}
            >
              Add your first integration
            </Button>
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState
          variant="no-results"
          title="No integrations match"
          description="Try another search, or a different status."
          action={
            <Button variant="outline" onClick={clearFilters}>
              Clear search and filter
            </Button>
          }
        />
      ) : (
        <>
          <DataTable
            layout="fixed"
            columns={INVENTORY_COLUMNS}
            data={visible}
            getRowId={(row) => row.integration.id}
            caption="Integrations"
            textSize="small"
            /* No `nowrap`: these cells carry prose and pills, so they wrap to
               their column instead of clipping at its edge. */
            pinFirstColumn
          />
          {filtering ? (
            <p role="status" className="text-xs text-pb-text-muted">
              Showing {visible.length} of {rows.length} integrations.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The status filter's options.
 *
 * `Connector unavailable` is offered only when the tenant actually holds one.
 * A filter that can only ever produce an empty list is a control that teaches
 * the reader nothing, and this state is rare by design — a connector goes
 * unavailable when a deployment stops registering it.
 */
function filterOptions(
  rows: readonly InventoryRow[],
): { value: LifecycleFilter; label: string }[] {
  const unavailable = rows.some(
    (row) => row.lifecycle === "connector-unavailable",
  );
  return [
    { value: "all", label: "All" },
    { value: "enabled", label: LIFECYCLE_LABELS.enabled },
    { value: "not-enabled", label: LIFECYCLE_LABELS["not-enabled"] },
    ...(unavailable
      ? [
          {
            value: "connector-unavailable" as const,
            label: LIFECYCLE_LABELS["connector-unavailable"],
          },
        ]
      : []),
  ];
}

/* The inventory as the Direction B table (PB-5178, d9/d11): every column is a
   field the two list reads carry. The name is a real link — the way into an
   integration, reachable by keyboard and openable in a new tab — and nothing
   else in the row is interactive. The lifecycle switch that used to sit here
   lives in the integration's own Overview, where the copy has room to say what
   turning it off does. */
const INVENTORY_COLUMNS: ReadonlyArray<DataTableColumn<InventoryRow>> = [
  {
    id: "integration",
    width: "24%",
    header: "Integration",
    cardRole: "title",
    cell: ({ integration }) => (
      <span className="block min-w-0">
        {/* Look row OK-52: the design-system entity Link on the router; the
            name keeps its hover underline and turns the entity cyan. */}
        <Link asChild variant="entity" display="block" className="truncate">
          <RouterLink to={adminIntegrationRoute(integration.id)}>
            {integration.displayName}
          </RouterLink>
        </Link>
        {integration.description === null ? null : (
          <span className="block truncate text-dense-1 text-pb-text-muted">
            {integration.description}
          </span>
        )}
      </span>
    ),
  },
  {
    id: "type",
    width: "20%",
    header: "Type",
    cardRole: "subtitle",
    /* The TEMPLATE's own name plus the contract's protocol. Omitted rather
       than guessed when the catalogue did not return the template. */
    cell: ({ template }) =>
      template === undefined
        ? "—"
        : `${template.displayName} · ${PROTOCOL_LABELS[template.protocol]}`,
  },
  {
    id: "status",
    width: "21%",
    header: "Status",
    cardRole: "badge",
    /* The pill alone, as the artboard draws it. What each state MEANS is
       stated once in the section's aside (the legend card), not repeated on
       every row — a sentence per row was the widest thing in the table. */
    cell: ({ lifecycle }) => (
      <StatusPill tone={LIFECYCLE_TONES[lifecycle]}>
        {LIFECYCLE_LABELS[lifecycle]}
      </StatusPill>
    ),
  },
  {
    id: "details",
    width: "35%",
    header: "Details",
    /* Truncates rather than clipping (PB-5436): a long external tenant id ran
       past the column with no ellipsis at every width. The full text stays in
       the cell for assistive tech and in the title for a pointer. */
    cell: ({ integration }) => {
      const details = [
        connectionModeLabel(integration.connectionMode),
        integration.externalTenantId,
      ]
        .filter((fact): fact is string => fact !== null)
        .join(" · ");
      return (
        <span className="block truncate text-pb-text-secondary" title={details}>
          {details}
        </span>
      );
    },
  },
];
